import { createContractCenterRequest } from "@/services/request";
import type { ApiRequestOptions } from "./types/common";
import type {
  ApprovalStatus,
  Contract,
  ContractApprovalDetail,
  ContractApprovalHistoryItem,
  ContractApprovalTask,
  ContractApprovalTaskAction,
  ContractApprovalTaskDetail,
  CreateContractInput,
  ContractAssociation,
  ContractDetail,
  ContractFulfillment,
  ContractItem,
  ContractListParams,
  ContractListItem,
  ContractOperationLog,
  ContractParty,
  ContractSettlementPlan,
  ContractSettlementFinanceSyncResult,
  ContractSettlementPlanList,
  ContractSettlementPlanAmountAdjustment,
  ContractSettlementPlanAmountAdjustmentResponse,
  ContractInvoice,
  ContractInvoiceApplication,
  ContractInvoiceOverview,
  ContractVersion,
  CompanyEntityOption,
  PageResult,
} from "./types/contract-center";

const request = createContractCenterRequest();
const jsonOptions = { headers: { "Content-Type": "application/json" } };

export async function listContracts(
  params: ContractListParams,
  options?: ApiRequestOptions
) {
  return request<PageResult<ContractListItem>>("/bff/contracts", {
    method: "GET",
    params,
    ...(options || {}),
  });
}

export async function listMyContracts(
  params: ContractListParams,
  options?: ApiRequestOptions
) {
  return request<PageResult<ContractListItem>>('/bff/contracts/mine', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export function getContract(uuid: string, options?: ApiRequestOptions) {
  return request<ContractDetail>(`/bff/contracts/${uuid}`, {
    method: "GET",
    ...(options || {}),
  });
}

export function createContract(
  data: CreateContractInput,
  options?: ApiRequestOptions
) {
  return request<Contract>("/bff/contracts", {
    method: "POST",
    data,
    ...jsonOptions,
    ...(options || {}),
  });
}

export function suggestContractNo(
  contractTypeUuid: string,
  options?: ApiRequestOptions
) {
  return request<{ contractNo: string }>("/bff/contracts/next-contract-no", {
    method: "POST",
    data: { contractTypeUuid },
    ...jsonOptions,
    ...(options || {}),
  });
}

export function updateContract(
  uuid: string,
  data: Record<string, unknown>,
  options?: ApiRequestOptions
) {
  return request<Contract>(`/bff/contracts/${uuid}`, {
    method: "PATCH",
    data,
    ...jsonOptions,
    ...(options || {}),
  });
}

export function deleteContract(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`/bff/contracts/${uuid}`, {
    method: "DELETE",
    ...(options || {}),
  });
}

export function listContractParties(uuid: string) {
  return request<ContractParty[]>(`/bff/contracts/${uuid}/parties`, {
    method: "GET",
  });
}

export function addContractParty(
  uuid: string,
  signingPartyUuid: string,
  sortOrder?: number
) {
  return request<ContractParty>(`/bff/contracts/${uuid}/parties`, {
    method: "POST",
    data: {
      signingPartyUuid,
      ...(sortOrder === undefined ? {} : { sortOrder: String(sortOrder) }),
    },
    ...jsonOptions,
  });
}

export function updateContractParty(
  uuid: string,
  partyUuid: string,
  data: Record<string, unknown>
) {
  return request<ContractParty>(`/bff/contracts/${uuid}/parties/${partyUuid}`, {
    method: "PATCH",
    data,
    ...jsonOptions,
  });
}

export function removeContractParty(uuid: string, partyUuid: string) {
  return request<void>(`/bff/contracts/${uuid}/parties/${partyUuid}`, {
    method: "DELETE",
  });
}

export function listContractItems(uuid: string) {
  return request<ContractItem[]>(`/bff/contracts/${uuid}/items`, {
    method: "GET",
  });
}

export function createContractItem(
  uuid: string,
  data: Record<string, unknown>
) {
  return request<ContractItem>(`/bff/contracts/${uuid}/items`, {
    method: "POST",
    data,
    ...jsonOptions,
  });
}

export function updateContractItem(
  uuid: string,
  itemUuid: string,
  data: Record<string, unknown>
) {
  return request<ContractItem>(`/bff/contracts/${uuid}/items/${itemUuid}`, {
    method: "PATCH",
    data,
    ...jsonOptions,
  });
}

export function removeContractItem(uuid: string, itemUuid: string) {
  return request<void>(`/bff/contracts/${uuid}/items/${itemUuid}`, {
    method: "DELETE",
  });
}

export function listVersions(uuid: string) {
  return request<ContractVersion[]>(`/bff/contracts/${uuid}/versions`, {
    method: "GET",
  });
}

export function uploadVersion(
  uuid: string,
  file: File,
  versionPurpose: "NORMAL" | "SIGNED"
) {
  const data = new FormData();
  data.append("file", file);
  data.append("versionPurpose", versionPurpose);
  return request<ContractVersion>(`/bff/contracts/${uuid}/versions`, {
    method: "POST",
    data,
  });
}

export function deleteVersion(uuid: string, versionUuid: string) {
  return request<void>(`/bff/contracts/${uuid}/versions/${versionUuid}`, {
    method: "DELETE",
  });
}

export function getVersionUrl(
  uuid: string,
  versionUuid: string,
  mode: "preview" | "download"
) {
  return request<{ url: string; urlMode: string }>(
    `/bff/contracts/${uuid}/versions/${versionUuid}/${mode}`,
    { method: "GET" }
  );
}

export function listAttachments(uuid: string) {
  return request<import("./types/contract-center").ContractAttachment[]>(
    `/bff/contracts/${uuid}/attachments`,
    { method: "GET" }
  );
}

export function uploadAttachment(uuid: string, file: File, category?: string) {
  const data = new FormData();
  data.append("file", file);
  if (category) data.append("category", category);
  return request<import("./types/contract-center").ContractAttachment>(
    `/bff/contracts/${uuid}/attachments`,
    { method: "POST", data }
  );
}

export function deleteAttachment(uuid: string, attachmentUuid: string) {
  return request<void>(`/bff/contracts/${uuid}/attachments/${attachmentUuid}`, {
    method: "DELETE",
  });
}

export function getAttachmentUrl(
  uuid: string,
  attachmentUuid: string,
  mode: "preview" | "download"
) {
  return request<{ url: string; urlMode: string }>(
    `/bff/contracts/${uuid}/attachments/${attachmentUuid}/${mode}`,
    { method: "GET" }
  );
}

export function listFulfillments(uuid: string) {
  return request<ContractFulfillment[]>(`/bff/contracts/${uuid}/fulfillments`, {
    method: "GET",
  });
}

export function createFulfillment(uuid: string, data: Record<string, unknown>) {
  return request<ContractFulfillment>(`/bff/contracts/${uuid}/fulfillments`, {
    method: "POST",
    data,
    ...jsonOptions,
  });
}

export function updateFulfillment(
  uuid: string,
  fulfillmentUuid: string,
  data: Record<string, unknown>
) {
  return request<ContractFulfillment>(
    `/bff/contracts/${uuid}/fulfillments/${fulfillmentUuid}`,
    {
      method: "PATCH",
      data,
      ...jsonOptions,
    }
  );
}

export function deleteFulfillment(uuid: string, fulfillmentUuid: string) {
  return request<void>(
    `/bff/contracts/${uuid}/fulfillments/${fulfillmentUuid}`,
    { method: "DELETE" }
  );
}

export function listSettlementPlans(uuid: string) {
  return request<ContractSettlementPlanList>(
    `/bff/contracts/${uuid}/settlement-plans`,
    { method: "GET" },
  );
}

export function createSettlementPlan(
  uuid: string,
  data: Record<string, unknown>,
) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans`,
    { method: "POST", data, ...jsonOptions },
  );
}

export function updateSettlementPlan(
  uuid: string,
  planUuid: string,
  data: Record<string, unknown>,
) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}`,
    { method: "PATCH", data, ...jsonOptions },
  );
}

export function deleteSettlementPlan(uuid: string, planUuid: string) {
  return request<void>(`/bff/contracts/${uuid}/settlement-plans/${planUuid}`, {
    method: "DELETE",
  });
}

export function readySettlementPlan(uuid: string, planUuid: string) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/ready`,
    { method: "POST" },
  );
}

export function cancelSettlementPlan(uuid: string, planUuid: string) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/cancel`,
    { method: "POST" },
  );
}

export function recognizeSettlementPlan(uuid: string, planUuid: string) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/recognize`,
    { method: "POST" },
  );
}

export function syncSettlementPlanToFinance(uuid: string, planUuid: string) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/sync-finance`,
    { method: "POST" },
  );
}

export function refreshSettlementPlanFinanceExecution(
  uuid: string,
  planUuid: string,
) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/refresh-finance-execution`,
    { method: "POST" },
  );
}

export function retrySettlementPlanFinanceSync(
  uuid: string,
  planUuid: string,
) {
  return request<ContractSettlementPlan>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/retry-finance-sync`,
    { method: "POST" },
  );
}

export function syncContractSettlementFinance(uuid: string) {
  return request<ContractSettlementFinanceSyncResult>(
    `/bff/contracts/${uuid}/settlement-plans/sync-finance`,
    { method: "POST" },
  );
}

export function listCompanyEntities() {
  return request<CompanyEntityOption[]>("/bff/company-entities", {
    method: "GET",
  });
}

export function getContractInvoiceOverview(uuid: string) {
  return request<ContractInvoiceOverview>(`/bff/contracts/${uuid}/invoice-overview`, {
    method: 'GET',
  });
}

export function createContractInvoiceApplication(
  uuid: string,
  data: {
    invoiceType?: string;
    remark?: string;
    items: Array<{ settlementPlanUuid: string; requestedAmount: string }>;
  },
  idempotencyKey: string,
) {
  return request<ContractInvoiceApplication>(`/bff/contracts/${uuid}/invoice-applications`, {
    method: 'POST',
    data,
    headers: { ...jsonOptions.headers, 'Idempotency-Key': idempotencyKey },
  });
}

export function getContractInvoiceApplication(uuid: string, applicationUuid: string) {
  return request<ContractInvoiceApplication>(
    `/bff/contracts/${uuid}/invoice-applications/${applicationUuid}`,
    { method: 'GET' },
  );
}

export function listSettlementPlanInvoices(uuid: string, settlementPlanUuid: string) {
  return request<ContractInvoice[]>(
    `/bff/contracts/${uuid}/settlement-plans/${settlementPlanUuid}/invoices`,
    { method: 'GET' },
  );
}

export function createSettlementPlanAmountAdjustment(
  uuid: string,
  planUuid: string,
  data: { targetPlannedAmount: string; reason: string },
  idempotencyKey: string,
) {
  return request<ContractSettlementPlanAmountAdjustmentResponse>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/amount-adjustments`,
    {
      method: 'POST',
      data,
      headers: { ...jsonOptions.headers, 'Idempotency-Key': idempotencyKey },
    },
  );
}

export function listSettlementPlanAmountAdjustments(
  uuid: string,
  planUuid: string,
) {
  return request<ContractSettlementPlanAmountAdjustment[]>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/amount-adjustments`,
    { method: 'GET' },
  );
}

export function getSettlementPlanAmountAdjustment(
  uuid: string,
  planUuid: string,
  adjustmentUuid: string,
) {
  return request<{
    adjustment: ContractSettlementPlanAmountAdjustment;
    plan?: ContractSettlementPlan;
  }>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/amount-adjustments/${adjustmentUuid}`,
    { method: 'GET' },
  );
}

export function retrySettlementPlanAmountAdjustment(
  uuid: string,
  planUuid: string,
  adjustmentUuid: string,
) {
  return request<ContractSettlementPlanAmountAdjustmentResponse>(
    `/bff/contracts/${uuid}/settlement-plans/${planUuid}/amount-adjustments/${adjustmentUuid}/retry`,
    { method: 'POST' },
  );
}

export function completeFulfillment(
  uuid: string,
  fulfillmentUuid: string,
  data: Record<string, unknown>
) {
  return request<ContractFulfillment>(
    `/bff/contracts/${uuid}/fulfillments/${fulfillmentUuid}/complete`,
    { method: "POST", data, ...jsonOptions }
  );
}

export function listAssociations(uuid: string) {
  return request<ContractAssociation[]>(`/bff/contracts/${uuid}/associations`, {
    method: "GET",
  });
}

export function listOperationLogs(uuid: string, pageNo = 1, pageSize = 20) {
  return request<PageResult<ContractOperationLog>>(
    `/bff/contracts/${uuid}/operation-logs`,
    {
      method: "GET",
      params: { pageNo, pageSize },
    }
  );
}

export function submitApproval(uuid: string, remark?: string) {
  return request<Contract>(`/bff/contracts/${uuid}/submit`, {
    method: "POST",
    data: remark ? { remark } : {},
    ...jsonOptions,
  });
}

export function getApproval(uuid: string) {
  return request<ContractApprovalDetail>(`/bff/contracts/${uuid}/approval`, {
    method: "GET",
  });
}

export function refreshApproval(uuid: string) {
  return request<ContractApprovalDetail>(
    `/bff/contracts/${uuid}/approval/refresh`,
    { method: "POST" }
  );
}

export function getApprovalHistory(uuid: string) {
  return request<ContractApprovalHistoryItem[]>(
    `/bff/contracts/${uuid}/approval/history`,
    {
      method: "GET",
    }
  );
}

export function listApprovalTasks(type: 'todo' | 'done', params?: Record<string, unknown>) {
  return request<{ items: ContractApprovalTask[]; total: number; page: number; limit: number }>(
    `/bff/contracts/approval-tasks/${type}`,
    { method: 'GET', params },
  );
}

export function getApprovalTaskDetail(taskId: string) {
  return request<ContractApprovalTaskDetail>(`/bff/contracts/approval-tasks/${encodeURIComponent(taskId)}/detail`, { method: 'GET' });
}

export function performApprovalTask(taskId: string, action: ContractApprovalTaskAction, comment?: string) {
  return request<unknown>(`/bff/contracts/approval-tasks/${encodeURIComponent(taskId)}/action`, {
    method: 'POST', data: { action, ...(comment ? { comment } : {}) }, ...jsonOptions,
  });
}

export function batchPerformApprovalTasks(taskIds: string[], action: ContractApprovalTaskAction, comment?: string) {
  return request<unknown>('/bff/contracts/approval-tasks/batch-action', {
    method: 'POST', data: { taskIds, action, ...(comment ? { comment } : {}) }, ...jsonOptions,
  });
}

export function confirmSign(
  uuid: string,
  officialVersionUuid: string,
  signedDate: string
) {
  return request<Contract>(`/bff/contracts/${uuid}/confirm-sign`, {
    method: "POST",
    data: { officialVersionUuid, signedDate },
    ...jsonOptions,
  });
}

export function activateContract(uuid: string, actualEffectiveDate: string) {
  return request<Contract>(`/bff/contracts/${uuid}/activate`, {
    method: "POST",
    data: { actualEffectiveDate },
    ...jsonOptions,
  });
}

export function completeContract(
  uuid: string,
  completionDate: string,
  completionRemark?: string
) {
  return request<Contract>(`/bff/contracts/${uuid}/complete`, {
    method: "POST",
    data: { completionDate, ...(completionRemark ? { completionRemark } : {}) },
    ...jsonOptions,
  });
}

export function terminateContract(uuid: string, data: Record<string, unknown>) {
  return request<Contract>(`/bff/contracts/${uuid}/terminate`, {
    method: "POST",
    data,
    ...jsonOptions,
  });
}

export function voidContract(uuid: string, voidRemark?: string) {
  return request<Contract>(`/bff/contracts/${uuid}/void`, {
    method: "POST",
    data: voidRemark ? { voidRemark } : {},
    ...jsonOptions,
  });
}

export const isApprovalPending = (status?: ApprovalStatus) =>
  status === "PENDING";
